/**
 * ULD批量外放管理Modal组件
 * 支持批量外放和批量撤销外放操作
 */
import React, { useState, useMemo } from 'react';
import {
    Modal,
    Form,
    Input,
    Button,
    Alert,
    Space,
    Typography,
    Divider,
    Table,
    Tag,
    Statistic,
    Row,
    Col,
    Card,
    message
} from 'antd';
import { ExportOutlined, ImportOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;
const { TextArea } = Input;

const UldBatchDeliveryModal = ({
    visible,
    onCancel,
    onConfirm,
    loading = false,
    selectedUlds = [],
    mode = 'deliver' // 'deliver' | 'revoke'
}) => {
    const [form] = Form.useForm();
    const [confirmStep, setConfirmStep] = useState(false);
    const [formData, setFormData] = useState(null); // 保存表单数据

    const isDelivery = mode === 'deliver';
    const isRevoke = mode === 'revoke';

    // 分析选中的ULD
    const uldAnalysis = useMemo(() => {
        if (!selectedUlds.length) return { eligible: [], ineligible: [] };

        const eligible = [];
        const ineligible = [];

        selectedUlds.forEach(uld => {
            if (isDelivery) {
                // 外放条件：IN_STOCK且未外放
                if (uld.stock?.core_status === 'IN_STOCK' && !uld.master?.delivered_to_consignee) {
                    eligible.push(uld);
                } else {
                    const reason = uld.stock?.core_status !== 'IN_STOCK' 
                        ? `状态为${uld.stock?.core_status}` 
                        : '已外放';
                    ineligible.push({ ...uld, reason });
                }
            } else {
                // 撤销条件：已外放
                if (uld.master?.delivered_to_consignee) {
                    eligible.push(uld);
                } else {
                    ineligible.push({ ...uld, reason: '未外放' });
                }
            }
        });

        return { eligible, ineligible };
    }, [selectedUlds, isDelivery]);

    const handleSubmit = async () => {
        try {
            if (!confirmStep) {
                const values = await form.validateFields();
                setFormData(values);
                setConfirmStep(true);
                return;
            }
            
            const eligibleIds = uldAnalysis.eligible.map(uld => uld.master.id);
            
            let requestData;
            if (mode === 'deliver') {
                requestData = {
                    uld_ids: eligibleIds,
                    consignee_name: formData.consigneeName,
                    remarks: formData.remarks
                };
            } else {
                requestData = {
                    uld_ids: eligibleIds,
                    reason: formData.reason
                };
            }
            
            const confirmed = await onConfirm(requestData);
            if (confirmed === false) {
                return;
            }
            
            setConfirmStep(false);
            setFormData(null);
            form.resetFields();
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            const detail = error.response?.data?.detail || error.response?.data?.message || error.message;
            message.error(typeof detail === "string" ? detail : "批量外放操作失败，请稍后重试");
        }
    };

    const handleCancel = () => {
        setConfirmStep(false);
        setFormData(null);
        form.resetFields();
        onCancel();
    };

    const handleBack = () => {
        setConfirmStep(false);
        // 不清空formData，这样用户返回时数据还在
    };

    const getModalTitle = () => {
        if (confirmStep) {
            return isDelivery ? '确认批量外放ULD' : '确认批量撤销外放';
        }
        return isDelivery ? '批量外放ULD给收货人' : '批量撤销ULD外放';
    };

    const getIcon = () => {
        return isDelivery ? <ExportOutlined /> : <ImportOutlined />;
    };

    const getStatusColor = (status) => {
        const statusColors = {
            'IN_STOCK': 'green',
            'IN_TRANSIT': 'blue',
            'OUT_OF_SERVICE': 'orange',
            'RETIRED': 'red',
            'LOST': 'purple'
        };
        return statusColors[status] || 'default';
    };

    // 表格列定义
    const columns = [
        {
            title: 'ULD识别码',
            dataIndex: ['master', 'uld_identifier'],
            key: 'uld_identifier',
            width: 120,
            render: (text) => <Text code>{text}</Text>
        },
        {
            title: 'ULD类型',
            dataIndex: ['master', 'uld_type'],
            key: 'uld_type',
            width: 80
        },
        {
            title: '核心状态',
            dataIndex: ['stock', 'core_status'],
            key: 'core_status',
            width: 100,
            render: (status) => (
                <Tag color={getStatusColor(status)}>{status}</Tag>
            )
        },
        {
            title: '外放状态',
            key: 'delivery_status',
            width: 120,
            render: (_, record) => {
                if (record.master?.delivered_to_consignee) {
                    return <Tag color="orange">已外放</Tag>;
                }
                return <Tag color="green">未外放</Tag>;
            }
        }
    ];

    if (isDelivery) {
        columns.push({
            title: '收货人',
            dataIndex: ['master', 'consignee_name'],
            key: 'consignee_name',
            width: 120,
            render: (text) => text || '-'
        });
    }

    // 不符合条件的ULD表格列
    const ineligibleColumns = [
        ...columns,
        {
            title: '不可操作原因',
            dataIndex: 'reason',
            key: 'reason',
            width: 120,
            render: (reason) => <Text type="danger">{reason}</Text>
        }
    ];

    return (
        <Modal
            title={
                <Space>
                    {getIcon()}
                    {getModalTitle()}
                </Space>
            }
            open={visible}
            onCancel={handleCancel}
            width={900}
            footer={null}
            destroyOnHidden
        >
            {!confirmStep ? (
                // 第一步：显示分析结果和输入表单
                <>
                    {/* 统计信息 */}
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col span={8}>
                            <Card size="small">
                                <Statistic
                                    title="总选中"
                                    value={selectedUlds.length}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card size="small">
                                <Statistic
                                    title="可操作"
                                    value={uldAnalysis.eligible.length}
                                    valueStyle={{ color: '#52c41a' }}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card size="small">
                                <Statistic
                                    title="不可操作"
                                    value={uldAnalysis.ineligible.length}
                                    valueStyle={{ color: '#ff4d4f' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* 警告信息 */}
                    {uldAnalysis.eligible.length === 0 && (
                        <Alert
                            type="error"
                            message="无可操作的ULD"
                            description={
                                isDelivery 
                                    ? "选中的ULD中没有符合外放条件的（需要IN_STOCK状态且未外放）"
                                    : "选中的ULD中没有符合撤销条件的（需要已外放状态）"
                            }
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {uldAnalysis.ineligible.length > 0 && (
                        <Alert
                            type="warning"
                            message={`${uldAnalysis.ineligible.length} 个ULD不符合操作条件`}
                            description="这些ULD将被跳过，只对符合条件的ULD执行操作"
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <Form
                        form={form}
                        layout="vertical"
                        requiredMark={false}
                    >
                        {isDelivery && (
                            <Form.Item
                                name="consigneeName"
                                label="收货人名称"
                                rules={[
                                    { required: true, message: '请输入收货人名称' },
                                    { max: 200, message: '收货人名称不能超过200个字符' }
                                ]}
                            >
                                <Input
                                    placeholder="请输入收货人名称（货代、物流公司等）"
                                    maxLength={200}
                                    showCount
                                />
                            </Form.Item>
                        )}

                        {isRevoke && (
                            <Form.Item
                                name="reason"
                                label="撤销原因"
                                rules={[
                                    { required: true, message: '请输入撤销原因' },
                                    { max: 500, message: '撤销原因不能超过500个字符' }
                                ]}
                            >
                                <TextArea
                                    rows={3}
                                    placeholder="请说明批量撤销外放的原因..."
                                    maxLength={500}
                                    showCount
                                />
                            </Form.Item>
                        )}

                        <Form.Item
                            name="remarks"
                            label={isDelivery ? "外放备注" : "补充说明"}
                        >
                            <TextArea
                                rows={3}
                                placeholder={isDelivery ? "选填，批量外放相关备注信息..." : "选填，补充说明信息..."}
                                maxLength={500}
                                showCount
                            />
                        </Form.Item>
                    </Form>

                    <Divider />

                    {/* 可操作的ULD列表 */}
                    {uldAnalysis.eligible.length > 0 && (
                        <>
                            <Title level={5}>
                                <InfoCircleOutlined style={{ color: '#52c41a' }} />
                                <span style={{ marginLeft: 8 }}>
                                    可{isDelivery ? '外放' : '撤销'}的ULD ({uldAnalysis.eligible.length}个)
                                </span>
                            </Title>
                            <Table
                                dataSource={uldAnalysis.eligible}
                                columns={columns}
                                rowKey="id"
                                pagination={false}
                                size="small"
                                scroll={{ y: 200 }}
                                style={{ marginBottom: 16 }}
                            />
                        </>
                    )}

                    {/* 不可操作的ULD列表 */}
                    {uldAnalysis.ineligible.length > 0 && (
                        <>
                            <Title level={5}>
                                <InfoCircleOutlined style={{ color: '#ff4d4f' }} />
                                <span style={{ marginLeft: 8 }}>
                                    不可{isDelivery ? '外放' : '撤销'}的ULD ({uldAnalysis.ineligible.length}个)
                                </span>
                            </Title>
                            <Table
                                dataSource={uldAnalysis.ineligible}
                                columns={ineligibleColumns}
                                rowKey="id"
                                pagination={false}
                                size="small"
                                scroll={{ y: 200 }}
                            />
                        </>
                    )}

                    <div style={{ textAlign: 'right', marginTop: 24 }}>
                        <Space>
                            <Button onClick={handleCancel}>
                                取消
                            </Button>
                            <Button 
                                type="primary" 
                                onClick={handleSubmit}
                                disabled={uldAnalysis.eligible.length === 0}
                            >
                                下一步
                            </Button>
                        </Space>
                    </div>
                </>
            ) : (
                // 第二步：确认操作
                <div>
                    <Alert
                        type="warning"
                        message={isDelivery ? "确认批量外放操作" : "确认批量撤销操作"}
                        description={
                            isDelivery 
                                ? `即将外放 ${uldAnalysis.eligible.length} 个ULD给收货人，外放后可用性将变为占用状态。`
                                : `即将撤销 ${uldAnalysis.eligible.length} 个ULD的外放状态，撤销后可用性将恢复为可用状态。`
                        }
                        style={{ marginBottom: 16 }}
                    />

                    <div style={{ padding: 16, backgroundColor: '#fafafa', borderRadius: 6, marginBottom: 16 }}>
                        <Title level={5}>操作详情</Title>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Text strong>操作类型：</Text>
                                <Text>{isDelivery ? '批量外放' : '批量撤销外放'}</Text>
                            </Col>
                            <Col span={12}>
                                <Text strong>影响ULD数量：</Text>
                                <Text>{uldAnalysis.eligible.length}个</Text>
                            </Col>
                        </Row>
                        {isDelivery && (
                            <div style={{ marginTop: 8 }}>
                                <Text strong>收货人：</Text>
                                <Text>{formData?.consigneeName}</Text>
                            </div>
                        )}
                        {isRevoke && (
                            <div style={{ marginTop: 8 }}>
                                <Text strong>撤销原因：</Text>
                                <Text>{formData?.reason}</Text>
                            </div>
                        )}
                        {formData?.remarks && (
                            <div style={{ marginTop: 8 }}>
                                <Text strong>备注：</Text>
                                <Text>{formData.remarks}</Text>
                            </div>
                        )}
                    </div>

                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={handleBack}>
                                上一步
                            </Button>
                            <Button onClick={handleCancel}>
                                取消
                            </Button>
                            <Button 
                                type="primary" 
                                danger={isRevoke}
                                loading={loading}
                                onClick={handleSubmit}
                            >
                                {isDelivery ? `确认外放 ${uldAnalysis.eligible.length} 个ULD` : `确认撤销 ${uldAnalysis.eligible.length} 个ULD`}
                            </Button>
                        </Space>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default UldBatchDeliveryModal; 